import React, {useState} from 'react';
import SpaceBetween from "../common/SpaceBetween"
import {Chip, Stack, Tooltip} from "@mui/material"
import AsyncButton from "../button/AsyncButton"
import {StopCircleOutlined} from "@mui/icons-material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import {QualityCheckRequests} from "../../api/requests/QualityCheckRequests"
import {useToasts} from "../../hooks/useToasts"
import QualityCheckProgressBar from "./QualityCheckProgressBar"
import {ProjectJson} from "../../api/json/ProjectJson"

interface QualityCheckSectionHeaderProps {
    project: ProjectJson
    qualityCheckJson: QualityCheckJson
    totalValidationExamples: number
    updateQualityCheck: (qualityCheckJson: QualityCheckJson) => void
    endpointStarting?: boolean
}

function QualityCheckSectionHeader({
                                       project,
                                       qualityCheckJson,
                                       totalValidationExamples,
                                       updateQualityCheck,
                                       endpointStarting,
                                   }: QualityCheckSectionHeaderProps) {
    const [running, setRunning] = useState(false)
    const [starting, setStarting] = useState(false)
    const [canceling, setCanceling] = useState(false)

    const toasts = useToasts()

    const handleRestart = () => {
        setStarting(true)
        QualityCheckRequests.restart(qualityCheckJson.id)
            .then(qualityCheckJson => {
                toasts.showMessage("Quality check started successfully.", "success")
                updateQualityCheck(qualityCheckJson)
                setRunning(true)
            })
            .catch(toasts.showError)
            .finally(() => setStarting(false))
    }

    const handleCancel = () => {
        setCanceling(true)
        QualityCheckRequests.cancel(qualityCheckJson.id)
            .then(qualityCheckJson => {
                toasts.showMessage("Quality check canceled.", "success")
                updateQualityCheck(qualityCheckJson)
                setRunning(false)
            })
            .catch(toasts.showError)
            .finally(() => setCanceling(false))
    }

    const stats = qualityCheckJson.stats

    const showCancel = !qualityCheckJson.completed && !qualityCheckJson.failed
    const showRunMissing = qualityCheckJson.completed && stats.completedTestRuns < totalValidationExamples && !running
    const showRunAgain = !!qualityCheckJson.failed && !running
    const showTopRow = showCancel || showRunMissing || showRunAgain || !!qualityCheckJson.canceled || !!qualityCheckJson.failed
    const showProgress = !qualityCheckJson.completed && !qualityCheckJson.failed

    if (!showTopRow && !showProgress) return null

    return (
        <Stack spacing={2}>
            {showTopRow &&
                <SpaceBetween sx={{alignItems: "center"}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {qualityCheckJson.canceled &&
                            <Chip variant="outlined" size="small" color="warning" label="Canceled"/>
                        }
                        {qualityCheckJson.failed &&
                            <Tooltip title={qualityCheckJson.failedReason ?? ""}>
                                <Chip variant="outlined" size="small" color="error" label="Failed"/>
                            </Tooltip>
                        }
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        {showCancel &&
                            <AsyncButton variant="text"
                                         color="secondary"
                                         size="small"
                                         waiting={canceling}
                                         endIcon={<StopCircleOutlined/>}
                                         onClick={handleCancel}>Cancel</AsyncButton>
                        }
                        {showRunMissing &&
                            <AsyncButton variant="text"
                                         color="secondary"
                                         size="small"
                                         endIcon={<PlayArrowIcon/>}
                                         waiting={starting}
                                         onClick={handleRestart}>Run missing</AsyncButton>
                        }
                        {showRunAgain &&
                            <AsyncButton variant="text"
                                         color="secondary"
                                         size="small"
                                         endIcon={<PlayArrowIcon/>}
                                         waiting={starting}
                                         onClick={handleRestart}>Run again</AsyncButton>
                        }
                    </Stack>
                </SpaceBetween>
            }

            {showProgress &&
                <QualityCheckProgressBar qualityCheckJson={qualityCheckJson}
                                         totalValidationExamples={project.stats.numberOfValidationRows}
                                         endpointStarting={endpointStarting}/>
            }
        </Stack>
    )
}

export default QualityCheckSectionHeader;
